import React from 'react'
import styled from 'styled-components'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { H2, Text } from 'app/common/typography'
import { Card } from './Card'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { MarketingAnimationShortDTO } from 'shared/types'
import { eventsDefinitions, pageCategoryMapping } from 'shared/store/metadata'
import { contentFormat } from 'shared/utils'

type Props = {
    secondary: Array<MarketingAnimationShortDTO>,
    title: string,
    subTitle?: string,
    customStyle?: string,
    padding?: string,
    background?: string,
    noRatio?: boolean,
    intl: IntlShape
}

const Cards = injectIntl((props: Props) => {
    const {
        secondary,
        title,
        subTitle,
        padding,
        background,
        noRatio,
        intl
    } = props

    const getMetaData = (card: MarketingAnimationShortDTO) => {
        const meta = {
            ...eventsDefinitions['SUGGESTION'],
            event_action:
                eventsDefinitions['SUGGESTION'].event_action +
                intl.formatMessage({ id: title }),
            event_label: card.title || card.subtitle
        }
        const ga4Meta = {
            ...eventsDefinitions.GA4_CONTENT_ACCESS,
            ...pageCategoryMapping(intl),
            cta_name: card.title || card.subtitle,
            operation_name: card.actionUrl
                ? card.actionUrl.split('/').pop()
                : '',
            content_format: contentFormat.CONTENT
        }
        return [meta, ga4Meta]
    }

    return (
        <Section padding={padding} background={background}>
            <ResponsiveContainer>
                {title && (
                    <H2>
                        <FormattedMessage id={title} />
                    </H2>
                )}
                {subTitle && (
                    <SubTitle>
                        <Text>
                            <FormattedMessage id={subTitle} />
                        </Text>
                    </SubTitle>
                )}
                <CardList>
                    {secondary.map((card, index) => (
                        <Card
                            key={index}
                            marketingAnimation={card}
                            maxWidth={secondary.length <= 2}
                            meta={getMetaData(card)}
                            noRatio={noRatio}
                        />
                    ))}
                </CardList>
            </ResponsiveContainer>
        </Section>
    )
})

const Section = styled.section`
    background: ${props => (props.background ? props.background : 'white')};
    padding: ${props => (props.padding ? props.padding : '30px 0')};
    ${ResponsiveContainer} {
        width: 100%;
        margin: 0;
        ${media.medium`
            margin:auto;
            width: calc(100% - 40px);
            padding-left:0;
        `};
    }
    ${H2}, p {
        margin: 5px auto 0 20px;
        text-align: left;
        ${media.medium`
            text-align: center;
            margin: 5px auto 0 0;
        `};
    }
`

const SubTitle = styled.p``

const CardList = styled.div`
    margin: 30px auto 0;
    overflow-x: scroll;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
    padding-left: 20px;
    padding-right: 20px;
    ::-webkit-scrollbar {
        display: none;
    }
    ${media.medium`
        white-space: normal;
        width: 100%;
        max-width: 1280px;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: flex-start;
    `};
`

export { Cards }
